/// <amd-module name="Wiki/Overview/Components/Overview" />
import * as React from "react";
import { getId } from "VSS/Core/Util/Object";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner } from "VSSUI/Spinner";
import { Splitter, SplitterDirection, SplitterElementPosition } from "VSSUI/Splitter";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, KeyCode } from "VSSUI/Util";
import { IZeroDataProps, ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { GitVersionDescriptor } from "Wiki/Clients/VC/Contracts/Git";
import { WikiPage, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { IWikiEditorState } from "Wiki/Editor/Contracts";
import { IHeaderMetaDataState } from "Wiki/HeaderMetaData/Interfaces/IHeaderMetadataTypes";
import { ActionCreatorsHub, NavPaneCollapseThreshold, PrintPageClickedEvent } from "Wiki/Overview/Actions/ActionCreatorsHub";
import { NavigationPane } from "Wiki/Overview/Components/NavigationPane";
import { WikiHeader } from "Wiki/Overview/Components/WikiHeader";
import * as Resources from "Wiki/Overview/Resources";
import { IOverviewState } from "Wiki/Overview/Stores/OverviewStore";
import { PagesHierarchyFetchedStatus, WikiTreeState } from "Wiki/Overview/Stores/WikiTreeStore";
import { getPageInfoWithName, ingestPageView, isFolderAssociatedPage } from "Wiki/Overview/Utils/WikiHelper";
import { WikiPageSwitchTtiLogger } from "Wiki/Overview/Utils/WikiPageSwitchTtiLogger";
import { WikiPageMenuItemsProvider } from "Wiki/Overview/WikiPageMenuItemsProvider";
import { WikiTreeDragDropProps } from "Wiki/PageTree/Components/WikiTree";
import { FilterTreeStore } from "Wiki/PageTree/Stores/FilterTreeStore";
import { IWikiPagePrintService } from "Wiki/PrintPage/IWikiPagePrintService";
import { WikiMarkdownRenderer } from "Wiki/Renderer/WikiMarkdownRenderer";
import { TemplateConstants, WikiPageCommentsFF } from "Wiki/ViewCommon/Constants";
import { ErrorState, WikiInfo } from "Wiki/ViewCommon/Contracts";
import { TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { getFileName } from "Wiki/ViewCommon/Utils/PathHelper";
import { isCodeEditEnabled, isCodeWiki, isIncludeEditingAllowed, publish } from "Wiki/ViewCommon/Utils/WikiHelper";

export interface IOverviewProps {
    editorState: IWikiEditorState | undefined;
    overviewState: IOverviewState;
    actionCreatorHub: ActionCreatorsHub;
    wikiInfo: WikiInfo;
    showCreateNewProjectWikiAction: boolean;
    showPublishCodeWikiAction: boolean;
    treeState: WikiTreeState;
    filterStore: FilterTreeStore;
    headerMetaDataState: IHeaderMetaDataState | undefined;
    menuItemProvider: WikiPageMenuItemsProvider;
    isHandleBrokenLinksEnabled: boolean;
    wikiPageSwitchTtiLogger: WikiPageSwitchTtiLogger;
    isPageCommentsReadOnly: boolean;
}

const wikiPrintIframe = "wiki-print-frame";
const wikiViewContainerClassName = "wiki-view-container";

export class Overview extends VssComponent<IOverviewProps> {
    private _printIframeId: string;

    constructor(props: IOverviewProps, context: IVssComponentContext) {
        super(props, context);

        this._printIframeId = getId(wikiPrintIframe);
    }

    public componentDidMount(): void {
        super.componentDidMount();
        // todo: log this telemetry for back navigation and page switch
        if (!this.props.overviewState.pageInfo) {
            publish(this.context.pageContext, TelemetryConstants.BrokenLinkErrorPageVisited, this.props.wikiInfo.wiki.type);
        } else {
            ingestPageView(
                this.context.pageContext,
                this.props.wikiInfo.wiki.id,
                this.props.overviewState.pageInfo.id,
                this.props.overviewState.pageInfo.path
            );
        }
    }

    public render(): JSX.Element {
        const { isCollapsed, currentSize } = this.props.overviewState.navPaneState;
        return (
            <div className="wiki-overview flex-grow flex-column">
                <iframe className={wikiPrintIframe} id={this._printIframeId} width="0%" height="0%" hidden />
                <Splitter
                    ariaLabel={Resources.SeparatorAriaLabel}
                    fixedSize={isCollapsed ? NavPaneCollapseThreshold : currentSize}
                    fixedElement={SplitterElementPosition.Near}
                    splitterDirection={SplitterDirection.Vertical}
                    onRenderFarElement={this._onRenderFarElement}
                    onRenderNearElement={this._onRenderNearElement}
                    onFixedSizeChanged={this.props.actionCreatorHub.onNavPaneResize}
                    nearElementClassName={css("wiki-left-pane flex-column flex-grow", isCollapsed && "justify-end wiki-nav-bg")}
                    farElementClassName="relative"
                />
            </div>
        );
    }

    private _onInitialRender = (): void => {
        this.props.wikiPageSwitchTtiLogger.endScenario();

        if (this.props.overviewState.pageInfo) {
            const properties: { [key: string]: any } = {};

            const queryIds = _getWitQueriesAttached(this.props.overviewState.pageInfo.content);
            if (queryIds && queryIds.length > 0) {
                properties["WitQueryIds"] = queryIds;
            }

            if (_hasMermaidDiagram(this.props.overviewState.pageInfo.content)) {
                properties["hasMermaidDiagram"] = true;
            }

            publish(this.context.pageContext, TelemetryConstants.PageViewed, this.props.wikiInfo.wiki.type, {
                pageId: this.props.overviewState.pageInfo.id,
                wikiId: this.props.wikiInfo.wiki.id,
                ...properties
            });
        }
    };

    private _onRenderFarElement = (): JSX.Element => {
        const { actionCreatorHub, headerMetaDataState, overviewState, treeState, wikiInfo } = this.props;

        if (overviewState.isEditing) {
            return (
                <>
                    {overviewState.isEditorModuleLoaded && this.props.editorState && this.props.editorState.pageContent != undefined ? (
                        <WrappedComponent
                            dependencies={["ms.vss-wiki-web.wiki-editor-content"]}
                            wrappedType={"wiki-editor"}
                            actionCreator={actionCreatorHub.getEditorAC()}
                            editorState={this.props.editorState}
                            wikiInfo={wikiInfo}
                            pageInfo={overviewState.tempPageInfo || (overviewState.pageInfo && getPageInfoWithName(overviewState.pageInfo))}
                            wikiPages={
                                isFeatureFlagEnabled(this.context.pageContext, "Wiki.TableOfSubPages", false)
                                    ? treeState.treeItemProvider.getAllPages()
                                    : []
                            }
                            anchor={overviewState.anchor}
                            onAnchorClick={actionCreatorHub.onAnchorClicked}
                            getWikiPage={
                                treeState.pagesFetchedStatus === PagesHierarchyFetchedStatus.Complete
                                    ? treeState.treeItemProvider.getWikiPageForPagePathIgnoreCase
                                    : undefined
                            }
                            getPagesForFiltering={actionCreatorHub.treeAC.getPagesForFiltering}
                            isHandleBrokenLinksEnabled={this.props.isHandleBrokenLinksEnabled}
                        />
                    ) : (
                        <Spinner className="wiki-editor-spinner" />
                    )}
                </>
            );
        } else {
            if (!overviewState.pageInfo) {
                return this._onRenderZeroDataComponent(this._getBaseZeroDataParams(Resources.PageNotFound, _getImagePath("page-not-found.svg")));
            } else {
                const pagePath = overviewState.pageInfo.path;

                if (_isIncludeFolder(this.context.pageContext, overviewState.pageInfo.path)) {
                    const baseParams = this._getBaseZeroDataParams(Resources.IncludesPrimaryMessage, _getImagePath("includes-folder.svg"));
                    return this._onRenderZeroDataComponent({
                        ...baseParams,
                        secondaryText: Resources.IncludesSecondaryMessage,
                        actionText: Resources.AddIncludeText,
                        actionType: ZeroDataActionType.ctaButton,
                        onActionClick: () => actionCreatorHub.addSubPage(TemplateConstants.TemplateDirectory)
                    });
                } else if (isFolderAssociatedPage(overviewState.pageInfo)) {
                    const pageTitle = getFileName(pagePath);
                    const baseParams = this._getBaseZeroDataParams(
                        Utils_String.format(Resources.IsAFolder, pageTitle),
                        _getImagePath("is-a-folder.svg")
                    );
                    return this._onRenderZeroDataComponent({
                        ...baseParams,
                        actionText: Resources.AddPageContent,
                        actionType: ZeroDataActionType.ctaButton,
                        onActionClick: () => actionCreatorHub.startEditingNewPage(pageTitle)
                    });
                } else {
                    return (
                        <ReadView
                            projectId={wikiInfo.wikiRepositoryData.repository.project.id}
                            repositoryId={wikiInfo.wikiRepositoryData.repository.id}
                            wiki={wikiInfo.wiki}
                            wikiVersion={wikiInfo.wikiVersion}
                            page={overviewState.pageInfo}
                            onUpdateLinkWitClick={actionCreatorHub.updateLinkedWit}
                            headerMetaDataState={headerMetaDataState}
                            anchor={overviewState.anchor}
                            onAnchorClick={actionCreatorHub.onAnchorClicked}
                            getWikiPage={
                                treeState.pagesFetchedStatus === PagesHierarchyFetchedStatus.Complete
                                    ? treeState.treeItemProvider.getWikiPageForPagePathIgnoreCase
                                    : undefined
                            }
                            menuItemProvider={this.props.menuItemProvider}
                            hasWikiContributePermission={wikiInfo.wikiPermissions.hasWikiContributePermission}
                            content={overviewState.pageInfo.content}
                            onInitialRender={this._onInitialRender}
                            isLoading={overviewState.isPageInfoLoading}
                            printFrame={this._printIframeId}
                            unfollowPage={overviewState.isPageUnfollow}
                            unsetUnfollowPageOption={actionCreatorHub.unsetUnfollowPageOption}
                            refreshFollowStatus={overviewState.refreshFollowStatus}
                            onFollowStatusRefreshed={actionCreatorHub.onFollowStatusRefreshed}
                            handleEmbedWorkItem={actionCreatorHub.handleEmbedWorkItem}
                            handleEmbedWorkItemFailure={actionCreatorHub.handleEmbedWorkItemFailure}
                            AddNewPage={this.props.actionCreatorHub.startEditingNewPage}
                            EditCurrentPage={this.props.actionCreatorHub.startEditingCurrentPage}
                            AddSubPageToCurrent={this.props.actionCreatorHub.addSubPageToCurrentPage}
                            TreeMoveDownOneLevel={this.props.actionCreatorHub.moveDownShortcutPressed}
                            TreeMoveUpOneLevel={this.props.actionCreatorHub.moveUpShortcutPressed}
                            clearError={actionCreatorHub.clearError}
                            errorState={overviewState.errorState}
                            PrintCurrentPage={this.props.actionCreatorHub.printCurrentPage}
                            isPageCommentsReadOnly={this.props.isPageCommentsReadOnly}
                            redirectToCode={actionCreatorHub.redirectToCode}
                            wikiPages={
                                isFeatureFlagEnabled(this.context.pageContext, "Wiki.TableOfSubPages", false)
                                    ? treeState.treeItemProvider.getAllPages()
                                    : []
                            }
                        />
                    );
                }
            }
        }
    };

    private _getBaseZeroDataParams = (primaryText: string, relativeImagePath: string): IZeroDataProps => {
        return {
            primaryText,
            imagePath: getAssetLocation(this.context.pageContext, relativeImagePath),
            imageAltText: ""
        };
    };

    private _onRenderZeroDataComponent = (props: IZeroDataProps): JSX.Element => {
        return (
            <div className="wiki-zerodata flex-column justify-center">
                <ZeroData {...props} />
            </div>
        );
    };

    private _onRenderNearElement = (): JSX.Element => {
        const { isCollapsed } = this.props.overviewState.navPaneState;
        let chevron: string;
        let collapseClass: string | undefined;
        let chevronAriaLabel: string;
        if (isCollapsed) {
            chevron = "DoubleChevronRight";
            collapseClass = "hidden";
            chevronAriaLabel = Resources.ShowMoreNavPane;
        } else {
            chevron = "DoubleChevronLeft";
            collapseClass = undefined;
            chevronAriaLabel = Resources.ShowLessNavPane;
        }
        const wikiTreeDragDropProps: WikiTreeDragDropProps = {
            canDrag: this.props.actionCreatorHub.treeAC.canDrag,
            canDrop: this.props.actionCreatorHub.treeAC.canDrop,
            onDrag: this.props.actionCreatorHub.treeAC.onDrag,
            onDrop: this.props.actionCreatorHub.treeAC.onDrop
        };

        return (
            <div className="flex-column flex-grow" role="navigation" aria-label={Resources.WikiOverview}>
                <div className={css("wiki-nav-bg flex-column flex-grow relative", collapseClass)}>
                    <NavigationPane
                        wikiInfo={this.props.wikiInfo}
                        showCreateNewProjectWikiAction={this.props.showCreateNewProjectWikiAction}
                        showPublishCodeWikiAction={this.props.showPublishCodeWikiAction}
                        treeState={this.props.treeState}
                        onPageSelected={this.props.actionCreatorHub.onPageSelected}
                        onFilteredPageSelected={this.props.actionCreatorHub.onFilteredPageSelected}
                        onPageToggled={this.props.actionCreatorHub.treeAC.onPageToggled}
                        onFilterChanged={this.props.actionCreatorHub.treeAC.onFilteringTextChanged}
                        onFilteringComplete={this.props.actionCreatorHub.treeAC.onFilteringComplete}
                        filterStore={this.props.filterStore}
                        menuItemProvider={this.props.menuItemProvider}
                        wikiTreeDragDropProps={wikiTreeDragDropProps}
                        isReorderInProgress={this.props.treeState.isReorderInProgress}
                        selectedPagePath={this.props.overviewState.pageInfo && this.props.overviewState.pageInfo.path}
                        isHandleBrokenLinksEnabled={this.props.isHandleBrokenLinksEnabled}
                        openInNewTab={this.props.actionCreatorHub.openInNewTab}
                    />
                </div>
                <div className={css("wiki-expander flex-grow", !isCollapsed && "hidden")} onClick={this._onChevronClick} />
                <div className={css("wiki-collapse-section flex-row flex-noshrink")}>
                    <Button
                        className={css("wiki-newpage-button flex-grow", collapseClass)}
                        iconProps={{ iconName: "Add" }}
                        subtle={true}
                        onClick={() => this.props.actionCreatorHub.startEditingNewPage()}
                        text={Resources.NewPageActionButton}
                        disabled={
                            !this.props.wikiInfo.wikiPermissions.hasWikiContributePermission ||
                            (isCodeWiki(this.props.wikiInfo.wiki) && !isCodeEditEnabled(this.context.pageContext))
                        }
                    />
                    <Tooltip text={chevronAriaLabel}>
                        <div
                            aria-label={chevronAriaLabel}
                            className="focus-treatment wiki-collapse-icon-effect fontSize flex-row"
                            onClick={this._onChevronClick}
                            onKeyDown={this._onChevronKeyDown}
                            role="button"
                            tabIndex={0}
                        >
                            <Icon
                                className={css(
                                    "wiki-collapse-icon flex-row flex-center flex-noshrink justify-center cursor-pointer",
                                    isCollapsed ? "collapsed" : "expanded"
                                )}
                                iconName={chevron}
                            />
                        </div>
                    </Tooltip>
                </div>
            </div>
        );
    };

    private _onChevronKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (event.which === KeyCode.space || event.which === KeyCode.enter) {
                this._onChevronClick();
                event.preventDefault();
            }
        }
    };

    private _onChevronClick = () => {
        this.props.actionCreatorHub.onToggleNavPaneVisibility();
    };
}

interface IReadViewProps {
    projectId: string;
    repositoryId: string;
    wiki: WikiV2;
    wikiVersion: GitVersionDescriptor;
    wikiPages: WikiPage[];
    page: WikiPage;
    headerMetaDataState: IHeaderMetaDataState | undefined;
    onUpdateLinkWitClick: (count: number) => void;
    content: string;
    anchor?: string;
    onAnchorClick?: (anchor?: string) => void;
    getWikiPage?: (pagePath: string) => WikiPage | undefined;
    menuItemProvider: WikiPageMenuItemsProvider;
    hasWikiContributePermission?: boolean;
    isLoading?: boolean;
    onInitialRender: () => void;
    printFrame: string;
    errorState?: ErrorState;
    clearError: () => void;
    handleEmbedWorkItem: (content: string, workItemId: number, workItemType: string, errorMessage: string) => void;
    handleEmbedWorkItemFailure: (errorMessage: string) => void;

    // shortcut props
    AddNewPage: () => void;
    EditCurrentPage: () => void;
    AddSubPageToCurrent: () => void;
    TreeMoveDownOneLevel: () => void;
    TreeMoveUpOneLevel: () => void;
    PrintCurrentPage: () => void;

    redirectToCode: (pagePath: string) => void;

    // folllow
    unfollowPage?: boolean;
    refreshFollowStatus?: boolean;
    onFollowStatusRefreshed?: () => void;
    isPageCommentsReadOnly: boolean;
    unsetUnfollowPageOption(): void;
}

class ReadView extends VssComponent<IReadViewProps> {
    private _markdownRendererRef = React.createRef<WikiMarkdownRenderer>();

    public componentDidMount(): void {
        super.componentDidMount();

        this._registerShortcuts(!!this.props.hasWikiContributePermission);
        this.addEventListener(document.body, PrintPageClickedEvent, this._populateFrameAndPrint);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        // unregisterShortcuts
        const shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        shortcutService.removeShortcutGroup(Resources.ViewShortcutGroupHeading);
    }

    public render(): JSX.Element {
        return (
            <div className={css("absolute-fill scroll-auto flex-column", wikiViewContainerClassName)}>
                {this.props.errorState && this.props.errorState.message && (
                    <MessageCard
                        severity={MessageCardSeverity.Error}
                        onDismiss={this.props.clearError}
                        className={"flex-noshrink"}
                        buttonProps={this.props.errorState.buttonProps}
                    >
                        {this.props.errorState.message}
                    </MessageCard>
                )}
                <WikiHeader
                    headerMetaDataState={this.props.headerMetaDataState}
                    onUpdateLinkWitClick={this.props.onUpdateLinkWitClick}
                    wiki={this.props.wiki}
                    page={this.props.page}
                    menuItemProvider={this.props.menuItemProvider}
                    hasWikiContributePermission={this.props.hasWikiContributePermission}
                    unfollowPage={this.props.unfollowPage}
                    refreshFollowStatus={this.props.refreshFollowStatus}
                    onFollowStatusRefreshed={this.props.onFollowStatusRefreshed}
                    unsetUnfollowPageOption={this.props.unsetUnfollowPageOption}
                    pagePath={this.props.page.path}
                    editCurrentPage={this.props.EditCurrentPage}
                    redirectToCode={this.props.redirectToCode}
                />
                {this.props.isLoading ? (
                    <Spinner />
                ) : (
                    // wrapping in div to fix the IE issue of comments component coming in between page content
                    <div>
                        <WikiMarkdownRenderer
                            wiki={this.props.wiki}
                            wikiVersion={this.props.wikiVersion}
                            wikiPages={this.props.wikiPages}
                            projectId={this.props.projectId}
                            repositoryId={this.props.repositoryId}
                            content={this.props.content}
                            anchor={this.props.anchor}
                            onAnchorClick={this.props.onAnchorClick}
                            getWikiPage={this.props.getWikiPage}
                            onInitialRender={this.props.onInitialRender}
                            ref={this._markdownRendererRef}
                            pagePath={this.props.page.path}
                            pageId={this.props.page.id}
                            scrollToElementClassName={wikiViewContainerClassName}
                            handleEmbedWorkItem={
                                this.props.hasWikiContributePermission &&
                                (!isCodeWiki(this.props.wiki) || isCodeEditEnabled(this.context.pageContext))
                                    ? this.props.handleEmbedWorkItem
                                    : undefined
                            }
                            handleEmbedWorkItemFailure={this.props.handleEmbedWorkItemFailure}
                        />
                        {this.props.page.id &&
                            (isFeatureFlagEnabled(this.context.pageContext, WikiPageCommentsFF) ||
                                isFeatureFlagEnabled(this.context.pageContext, "Wiki.PageViewsUX")) && <hr className="wiki-metadata-separator" />}
                        {this.props.page.id && isFeatureFlagEnabled(this.context.pageContext, "Wiki.PageViewsUX") && (
                            <WrappedComponent
                                dependencies={["ms.vss-wiki-web.wiki-page-views-content"]}
                                wrappedType={"Wiki.WikiPageViews"}
                                wikiId={this.props.wiki.id}
                                projectId={this.props.wiki.projectId}
                                pageId={this.props.page.id}
                            />
                        )}
                        {this.props.page.id && isFeatureFlagEnabled(this.context.pageContext, WikiPageCommentsFF) && (
                            <WrappedComponent
                                dependencies={["ms.vss-wiki-web.wiki-page-comments"]}
                                wrappedType={"wiki-pagecomments"}
                                wikiArtifact={{ wikiId: this.props.wiki.id, projectId: this.props.wiki.projectId, pageId: this.props.page.id }}
                                isReadOnly={this.props.isPageCommentsReadOnly}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }

    private _registerShortcuts = (hasContributePermissions: boolean): void => {
        const shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        if (hasContributePermissions) {
            shortcutService.registerShortcut(Resources.ViewShortcutGroupHeading, {
                combos: ["n"],
                description: Resources.PageShortcut_Add,
                action: () => this.props.AddNewPage()
            });

            shortcutService.registerShortcut(Resources.ViewShortcutGroupHeading, {
                combos: ["e"],
                description: Resources.PageShortcut_Edit,
                action: this.props.EditCurrentPage
            });

            shortcutService.registerShortcut(Resources.ViewShortcutGroupHeading, {
                combos: ["c"],
                description: Resources.PageShortcut_AddSubPage,
                action: this.props.AddSubPageToCurrent
            });

            shortcutService.registerShortcut(Resources.ViewShortcutGroupHeading, {
                combos: ["mod+down"],
                description: Resources.TreeShortcut_MoveDown,
                action: this.props.TreeMoveDownOneLevel
            });

            shortcutService.registerShortcut(Resources.ViewShortcutGroupHeading, {
                combos: ["mod+up"],
                description: Resources.TreeShortcut_MoveUp,
                action: this.props.TreeMoveUpOneLevel
            });
        }

        shortcutService.registerShortcut(Resources.ViewShortcutGroupHeading, {
            combos: ["mod+p"],
            description: Resources.PageShortcut_Print,
            action: this.props.PrintCurrentPage
        });
    };

    private _populateFrameAndPrint = (): void => {
        const { headerMetaDataState } = this.props;
        if (headerMetaDataState) {
            let printContent = "";

            if (this._markdownRendererRef.current) {
                const content = this._markdownRendererRef.current.getRenderedContent();
                if (content) {
                    printContent = content;
                }
            }

            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.trackPromise(contributionService.getService<IWikiPagePrintService>("ms.vss-wiki-web.wiki-printpage-service")).promise.then(
                (printPageService: IWikiPagePrintService) => {
                    if (printPageService && this.props.page) {
                        printPageService.populateFrameAndPrint(
                            this.props.printFrame,
                            getFileName(this.props.page.path),
                            headerMetaDataState,
                            printContent
                        );
                    }
                }
            );
        }
    };
}

function _isIncludeFolder(pageContext: IVssPageContext, path: string): boolean {
    return path === TemplateConstants.TemplateDirectory && isIncludeEditingAllowed(pageContext);
}

function _getImagePath(imageName: string): string {
    return "ms.vss-wiki-web/wiki-overview-content/Images/" + imageName;
}

function _hasMermaidDiagram(pageContent: string): boolean {
    const mermaidRegex = ":::[\\s]*mermaid[\\s\\r\\n]+[\\S]+[\\s\\S]*:::";
    const pattern = new RegExp(mermaidRegex);

    return pattern.test(pageContent);
}

function _getWitQueriesAttached(pageContent: string): string[] {
    const queryTableRegex = ":::[\\s]*query-table[\\s]+([\\S]+)[\\s]*:::";
    const pattern = new RegExp(queryTableRegex, "ig");
    let match: RegExpExecArray | null;
    const queryIds = [];
    while ((match = pattern.exec(pageContent))) {
        queryIds.push(match[1]);
    }
    return queryIds;
}
